import classNames from 'classnames';
import React from 'react';
import { NavLink } from 'react-router-dom';

import { Tooltip } from '../ui/Tooltip';
import { type NavigationItem } from './constants';

import styles from './Layout.module.css';

interface NavigationItemProps {
  item: NavigationItem;
  /** Mobile renders labels inline and has no hover, so it opts out. */
  showTooltip?: boolean;
}

/**
 * The rail is permanently icon-only, so the tooltip is the ONLY thing carrying
 * each item's name. It is not decoration — S3 found ten unlabeled,
 * near-identical icons unusable precisely because there was nothing to reveal
 * the label. Do not make it conditional.
 *
 * `wrapperTag="li"` keeps the list structure valid: this used to wrap the whole
 * `<li>` in Tooltip's `<div>`, putting a div between `<ul>` and `<li>` and
 * breaking list semantics for screen readers.
 */
const NavigationItem: React.FC<NavigationItemProps> = ({
  item,
  showTooltip = true
}) => {
  const renderNavLinkContent = ({ isActive }: { isActive: boolean }) => {
    const navLinkClasses = classNames(styles.navLink, {
      [styles.active]: isActive
    });

    const IconComponent = item.icon;

    return (
      <div className={navLinkClasses}>
        <span className={styles.navIcon}>
          <IconComponent size={20} />
        </span>
        <span className={styles.navText}>{item.title}</span>
      </div>
    );
  };

  const link = (
    <NavLink aria-label={item.title} to={item.path}>
      {renderNavLinkContent}
    </NavLink>
  );

  if (!showTooltip) {
    return <li className={styles.navItem}>{link}</li>;
  }

  return (
    <Tooltip
      className={styles.navTooltip}
      content={item.title}
      position="right"
      wrapperTag="li"
    >
      {link}
    </Tooltip>
  );
};

export default NavigationItem;
